import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../../components/Screen/Screen';
import { ScreenHeader } from '../../components/ScreenHeader/ScreenHeader';
import { ProgressIndicator } from '../../components/ProgressIndicator/ProgressIndicator';
import { Button } from '../../components/Button/Button';
import { TextInput } from '../../components/TextInput/TextInput';
import { copy, validation } from '../../constants';
import { useOnboardingStore } from '../../store/onboarding';
import { OnboardingAPI } from '../../api/onboarding';

export const Contact: React.FC = () => {
  const navigate = useNavigate();
  const { data, updateData } = useOnboardingStore();
  const [errors, setErrors] = useState({ name: '', email: '' });

  const name = data.name || '';
  const email = data.email || '';
  const phone = data.phone || '';

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limited = digits.slice(0, 10);
    
    // Format as (xxx)xxx-xxxx
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `(${limited.slice(0, 3)})${limited.slice(3)}`;
    } else {
      return `(${limited.slice(0, 3)})${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    updateData({ phone: formatted });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContinue = async () => {
    const newErrors = { name: '', email: '' };

    if (!name) {
      newErrors.name = validation.nameRequired;
    }

    if (!email) {
      newErrors.email = validation.emailRequired;
    } else if (!validateEmail(email)) {
      newErrors.email = validation.emailInvalid;
    }

    if (newErrors.name || newErrors.email) {
      setErrors(newErrors);
      return;
    }

    // build full onboarding payload and submit immediately
    setIsSubmitting(true);
    try {
      const onboardingData: any = {
        ...data,
        province: data.province || 'AB',
        timestamp: new Date().toISOString()
      };

      await OnboardingAPI.complete(Math.random().toString(36), onboardingData);
      navigate('/confirmation');
      // reset store after navigating away
      useOnboardingStore.getState().resetOnboarding();
    } catch (err) {
      console.error('Submission error:', err);
      setIsSubmitting(false);
      alert('There was a problem submitting your request. Please try again.');
    }
  };

  return (
    <Screen>
      <ProgressIndicator currentStep={8} totalSteps={8} />
      
      <ScreenHeader
        title={copy.contact.title}
        subtitle={copy.contact.subtitle}
      />
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
        <TextInput
          label={copy.contact.nameLabel}
          value={name}
          onChange={(value) => {
            setErrors({ ...errors, name: '' });
            updateData({ name: value });
          }}
          placeholder={copy.contact.namePlaceholder}
          type="text"
          required
          error={errors.name}
        />
        <TextInput
          label={copy.contact.emailLabel}
          value={email}
          onChange={(value) => {
            setErrors({ ...errors, email: '' });
            updateData({ email: value });
          }}
          placeholder={copy.contact.emailPlaceholder}
          type="email"
          required
          error={errors.email}
        />
        
        <TextInput
          label={copy.contact.phoneLabel}
          value={phone}
          onChange={handlePhoneChange}
          placeholder={copy.contact.phonePlaceholder}
          type="tel"
        />
      </div>
      
      <div style={{ marginTop: 'var(--spacing-lg)' }}>
        <Button
          variant="primary"
          size="large"
          fullWidth
          onClick={handleContinue}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : copy.contact.button}
        </Button>
      </div>
    </Screen>
  );
};
